import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { RAW_POLL_MS, BUCKETED_POLL_MS, type HistoryRange } from '../../api/history';

export type RangeKey = '1d' | '3d' | '7d' | '30d';

export const RANGE_KEYS: readonly RangeKey[] = ['1d', '3d', '7d', '30d'];

const DAY_MS = 24 * 60 * 60 * 1000;

// Long ranges blow past the /history 5000-row cap (~720 floor rows/day ⇒ 7 d
// ≈ 5040, 30 d ≈ 21 600), so they ask the API to bucket server-side. 720
// buckets is hourly at 30 d and ~15 min at 7 d. Short ranges stay raw.
const RANGE_SPEC: Record<RangeKey, { days: number; maxPoints?: number }> = {
  '1d':  { days: 1 },
  '3d':  { days: 3 },
  '7d':  { days: 7,  maxPoints: 720 },
  '30d': { days: 30, maxPoints: 720 },
};

// `from` is floored to the range's own POLL cadence and `to` is omitted (the
// server defaults it to now), so the react-query key turns over exactly in step
// with that range's refetchInterval instead of on every render. Quantising to a
// flat 60 s would defeat the bucketed backoff: a new key is a cache MISS, so a
// key that churns every minute re-fires the expensive scan every minute no
// matter what refetchInterval/staleTime say — they only suppress work WITHIN
// one key's lifetime.
export function useChartRange(initial: RangeKey = '1d'): {
  rangeKey: RangeKey;
  setRangeKey: (k: RangeKey) => void;
  range: HistoryRange;
} {
  const [rangeKey, setRangeKey] = useState<RangeKey>(initial);
  const spec = RANGE_SPEC[rangeKey];
  const quantum = spec.maxPoints !== undefined ? BUCKETED_POLL_MS : RAW_POLL_MS;
  // The clock is an external, mutable value, so it is read through
  // useSyncExternalStore rather than called during render. getSnapshot still
  // runs on every render, so the anchor picks up a clock change as soon as
  // anything re-renders — exactly as a bare Date.now() did — and the interval
  // makes the turnover happen on its own even if nothing else re-renders. The
  // snapshot is quantised, so it is stable within one poll cadence and cannot
  // loop.
  const subscribe = useMemo(
    () => (onChange: () => void) => {
      const id = setInterval(onChange, quantum);
      return () => { clearInterval(id); };
    },
    [quantum],
  );
  const getAnchor = useCallback(() => Math.floor(Date.now() / quantum) * quantum, [quantum]);
  const anchor = useSyncExternalStore(subscribe, getAnchor);
  const range: HistoryRange = {
    from: new Date(anchor - spec.days * DAY_MS).toISOString(),
    ...(spec.maxPoints !== undefined ? { maxPoints: spec.maxPoints } : {}),
  };
  return { rangeKey, setRangeKey, range };
}

// Chart-card props derived from a history response. Keys are present only
// when the API actually bucketed / truncated, so this stays safe under
// `exactOptionalPropertyTypes` and keeps five call sites from each repeating
// three conditional spreads.
export function bucketProps(
  data?: { bucketMs?: number | undefined; sampleCount?: number | undefined; truncated?: boolean | undefined },
): { bucketMs?: number; truncated?: boolean; sampleCount?: number } {
  return {
    ...(data?.bucketMs !== undefined ? { bucketMs: data.bucketMs } : {}),
    ...(data?.truncated ? { truncated: true, sampleCount: data.sampleCount ?? 0 } : {}),
  };
}
